import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';
import { useToast } from '../components/layout/ToastProvider';
import { motion, AnimatePresence } from 'framer-motion';
import FormModal from '../components/common/FormModal';
import ConfirmModal from '../components/common/ConfirmModal';

const { 
  FiFileText, FiPlus, FiSearch, FiMoreVertical, FiDownload, FiTrash2, 
  FiShare2, FiFolder, FiGrid, FiList, FiFilter, FiInfo, FiExternalLink,
  FiShield, FiUsers, FiClock, FiCheck, FiX, FiEdit2, FiCopy
} = FiIcons;

const CATEGORIES = ['All', 'Onboarding', 'Legal', 'Sales', 'Finance', 'Templates', 'Media'];

export default function Resources() {
  const { addToast } = useToast();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  // Modals
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Access State
  const [clients, setClients] = useState([]);
  const [courses, setCourses] = useState([]);
  const [grantedAccess, setGrantedAccess] = useState([]);

  useEffect(() => {
    fetchResources();
    fetchAccessData();
  }, []);

  const fetchResources = async () => {
    try {
      const { data, error } = await supabase
        .from('resources_2024')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setResources(data || []);
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessData = async () => {
    const [clientsRes, coursesRes] = await Promise.all([
      supabase.from('clients_2024').select('id, name'),
      supabase.from('courses_2024').select('id, title')
    ]);
    setClients(clientsRes.data || []);
    setCourses(coursesRes.data || []);
  };

  const fetchResourceAccess = async (resourceId) => {
    const { data } = await supabase
      .from('resource_access_2024')
      .select('*, client:clients_2024(name), course:courses_2024(title)')
      .eq('resource_id', resourceId);
    setGrantedAccess(data || []);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    const formData = new FormData(e.target);
    
    const payload = {
      title: formData.get('title'),
      description: formData.get('description'),
      category: formData.get('category'),
      file_type: formData.get('file_type'),
      file_url: formData.get('file_url') || 'https://images.unsplash.com/photo-1454165833767-027eeef1593e?w=800',
      size_bytes: Math.floor(Math.random() * 5000000) // Mock size
    };

    try {
      if (selectedResource?.id) {
        await supabase.from('resources_2024').update(payload).eq('id', selectedResource.id);
        addToast("Resource updated");
      } else {
        await supabase.from('resources_2024').insert([payload]);
        addToast("Resource added to vault");
      }
      setIsUploadModalOpen(false);
      fetchResources();
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const toggleAccess = async (type, targetId) => {
    try {
      const existing = grantedAccess.find(a => 
        (type === 'client' ? a.client_id === targetId : a.course_id === targetId)
      );

      if (existing) {
        await supabase.from('resource_access_2024').delete().eq('id', existing.id);
      } else {
        await supabase.from('resource_access_2024').insert([{
          resource_id: selectedResource.id,
          [type === 'client' ? 'client_id' : 'course_id']: targetId
        }]);
      }
      fetchResourceAccess(selectedResource.id);
    } catch (error) {
      addToast(error.message, 'error');
    }
  };

  const filteredResources = resources.filter(r => {
    const matchesSearch = r.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         r.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || r.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getFileIcon = (type) => {
    switch(type) {
      case 'pdf': return <SafeIcon icon={FiFileText} className="text-rose-400" />;
      case 'doc': return <SafeIcon icon={FiFileText} className="text-blue-400" />;
      case 'archive': return <SafeIcon icon={FiFolder} className="text-amber-400" />;
      default: return <SafeIcon icon={FiFileText} className="text-slate-400" />;
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div></div>;

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div>
          <h1 className="text-4xl font-black text-white tracking-tight">Resource Vault</h1>
          <p className="text-slate-400 mt-2 text-lg">Centralized library for high-performance assets and client templates.</p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input 
              type="text" 
              placeholder="Search resources..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-900 border border-slate-800 rounded-2xl pl-12 pr-4 py-3.5 text-sm text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all" 
            />
          </div>
          <button 
            onClick={() => { setSelectedResource(null); setIsUploadModalOpen(true); }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3.5 rounded-2xl font-bold flex items-center gap-2 transition-all shadow-xl shadow-indigo-600/20 shrink-0"
          >
            <SafeIcon icon={FiPlus} className="text-xl" /> Upload Asset
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-900/50 p-3 rounded-[24px] border border-slate-800">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide">
          {CATEGORIES.map(cat => (
            <button 
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${selectedCategory === cat ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-slate-800/50 p-1.5 rounded-xl">
          <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-white'}`}><SafeIcon icon={FiGrid} /></button>
          <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-white'}`}><SafeIcon icon={FiList} /></button>
        </div>
      </div>

      {/* Resource Grid/List */}
      <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' : 'space-y-3'}>
        {filteredResources.map((res) => (
          <motion.div 
            layout
            key={res.id} 
            className={`group bg-slate-900 border border-slate-800 hover:border-indigo-500/50 transition-all shadow-xl ${viewMode === 'grid' ? 'rounded-[32px] p-6 flex flex-col' : 'rounded-2xl p-4 flex items-center gap-6'}`}
          >
            {/* Thumbnail/Icon Area */}
            <div className={`${viewMode === 'grid' ? 'h-40 w-full mb-6' : 'w-14 h-14'} rounded-2xl bg-slate-800 flex items-center justify-center relative overflow-hidden shrink-0`}>
              <div className="text-4xl opacity-80 group-hover:scale-110 transition-transform duration-300">
                {getFileIcon(res.file_type)}
              </div>
              <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => { setSelectedResource(res); setIsUploadModalOpen(true); }} className="p-2 bg-black/60 text-white rounded-lg hover:bg-indigo-600 transition-colors"><SafeIcon icon={FiEdit2} className="text-xs" /></button>
                <button onClick={() => { setSelectedResource(res); setIsDeleteOpen(true); }} className="p-2 bg-rose-500/80 text-white rounded-lg hover:bg-rose-600 transition-colors"><SafeIcon icon={FiTrash2} className="text-xs" /></button>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-start mb-1">
                <h3 className="text-sm font-black text-white truncate pr-4">{res.title}</h3>
                <span className="text-[9px] font-black uppercase tracking-widest text-indigo-400 bg-indigo-400/10 px-2 py-0.5 rounded-md self-start">{res.file_type}</span>
              </div>
              <p className={`text-xs text-slate-500 mb-4 line-clamp-2 leading-relaxed ${viewMode === 'grid' ? '' : 'hidden md:block'}`}>{res.description}</p>
              
              <div className="flex items-center gap-4 text-[10px] font-bold text-slate-600">
                <span className="flex items-center gap-1.5"><SafeIcon icon={FiDownload} className="text-indigo-500" /> {res.download_count}</span>
                <span className="flex items-center gap-1.5"><SafeIcon icon={FiClock} /> {new Date(res.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Action Area */}
            <div className={`flex gap-2 ${viewMode === 'grid' ? 'mt-6 pt-6 border-t border-slate-800' : 'ml-auto'}`}>
              <button 
                onClick={() => { setSelectedResource(res); fetchResourceAccess(res.id); setIsAccessModalOpen(true); }}
                className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all border border-slate-700"
              >
                <SafeIcon icon={FiShield} /> Share
              </button>
              <a 
                href={res.file_url} 
                target="_blank"
                rel="noopener noreferrer"
                onClick={async () => {
                  await supabase.rpc('increment_download_count', { row_id: res.id });
                  fetchResources();
                }}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-indigo-600/20"
              >
                <SafeIcon icon={FiDownload} /> Get
              </a>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Upload/Edit Modal */}
      <FormModal 
        isOpen={isUploadModalOpen} 
        onClose={() => setIsUploadModalOpen(false)} 
        title={selectedResource ? "Edit Resource" : "Upload New Asset"} 
        onSubmit={handleUpload} 
        loading={modalLoading}
      >
        <div className="space-y-5">
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Asset Title</label>
            <input name="title" defaultValue={selectedResource?.title} required className="w-full bg-slate-800 border-none rounded-xl px-4 py-3.5 text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500" placeholder="e.g. Sales Playbook PDF" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Category</label>
              <select name="category" defaultValue={selectedResource?.category} className="w-full bg-slate-800 border-none rounded-xl px-4 py-3.5 text-sm text-white outline-none appearance-none">
                {CATEGORIES.filter(c => c !== 'All').map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">File Type</label>
              <select name="file_type" defaultValue={selectedResource?.file_type} className="w-full bg-slate-800 border-none rounded-xl px-4 py-3.5 text-sm text-white outline-none appearance-none">
                <option value="pdf">PDF Document</option>
                <option value="doc">Word/Text</option>
                <option value="archive">Zip/Archive</option>
                <option value="image">Image/Asset</option>
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Description</label>
            <textarea name="description" defaultValue={selectedResource?.description} rows="3" className="w-full bg-slate-800 border-none rounded-xl px-4 py-3.5 text-sm text-white outline-none resize-none" placeholder="What is this resource for?"></textarea>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">External Link (Mock Upload)</label>
            <div className="flex gap-2">
               <input name="file_url" defaultValue={selectedResource?.file_url} className="flex-1 bg-slate-800 border-none rounded-xl px-4 py-3.5 text-sm text-white outline-none" placeholder="https://..." />
               <button type="button" className="p-3.5 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-colors"><SafeIcon icon={FiExternalLink} /></button>
            </div>
          </div>
        </div>
      </FormModal>

      {/* Access/Sharing Modal */}
      <FormModal
        isOpen={isAccessModalOpen}
        onClose={() => setIsAccessModalOpen(false)}
        title={`Access Management: ${selectedResource?.title}`}
        hideFooter
      >
        <div className="space-y-8">
          <section>
            <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
              <SafeIcon icon={FiUsers} className="text-indigo-400" /> Assign to Clients
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
              {clients.map(client => {
                const hasAccess = grantedAccess.some(a => a.client_id === client.id);
                return (
                  <button 
                    key={client.id}
                    onClick={() => toggleAccess('client', client.id)}
                    className={`w-full flex items-center justify-between p-3.5 rounded-2xl transition-all border ${hasAccess ? 'bg-indigo-600/10 border-indigo-500/50' : 'bg-slate-800/40 border-slate-700 hover:bg-slate-800'}`}
                  >
                    <span className={`text-sm font-bold ${hasAccess ? 'text-indigo-400' : 'text-slate-300'}`}>{client.name}</span>
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${hasAccess ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-slate-500'}`}>
                      <SafeIcon icon={hasAccess ? FiCheck : FiPlus} />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
              <SafeIcon icon={FiFolder} className="text-amber-400" /> Course-wide Access
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
              {courses.map(course => {
                const hasAccess = grantedAccess.some(a => a.course_id === course.id);
                return (
                  <button 
                    key={course.id}
                    onClick={() => toggleAccess('course', course.id)}
                    className={`w-full flex items-center justify-between p-3.5 rounded-2xl transition-all border ${hasAccess ? 'bg-indigo-600/10 border-indigo-500/50' : 'bg-slate-800/40 border-slate-700 hover:bg-slate-800'}`}
                  >
                    <span className={`text-sm font-bold ${hasAccess ? 'text-indigo-400' : 'text-slate-300'}`}>{course.title}</span>
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${hasAccess ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-slate-500'}`}>
                      <SafeIcon icon={hasAccess ? FiCheck : FiPlus} />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      </FormModal>

      <ConfirmModal 
        isOpen={isDeleteOpen} 
        onClose={() => setIsDeleteOpen(false)} 
        onConfirm={async () => {
          await supabase.from('resources_2024').delete().eq('id', selectedResource.id);
          fetchResources();
          setIsDeleteOpen(false);
        }}
        title="Remove Asset?"
        message="This will delete the resource and revoke access for all assigned clients and courses."
      />
    </div>
  );
}